import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, rupees } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ArrowLeft, ShieldAlert, Truck, ShieldCheck } from "lucide-react";

export default function ProductDetail() {
  const { id } = useParams();
  const [m, setM] = useState(null);
  const [qty, setQty] = useState(1);
  const { addToCart } = useCart() || { addToCart: () => {} };
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/medicines/${id}`).then(({ data }) => setM(data)).catch(() => setM(false));
  }, [id]);

  if (m === null) return <div className="min-h-screen flex items-center justify-center bg-[#EFE7D6]"><div className="w-8 h-8 border-2 border-[#1A362B] border-t-transparent rounded-full animate-spin"/></div>;
  if (!m) return <div className="min-h-screen flex items-center justify-center">Not found</div>;

  return (
    <div className="min-h-screen bg-[#EFE7D6]">
      <Header />
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10">
        <Link to="/shop" className="inline-flex items-center gap-2 label-caps text-xs text-stone-600 hover:text-[#1A362B] mb-8" data-testid="back-to-shop">
          <ArrowLeft className="w-4 h-4"/> Back to shop
        </Link>
        <div className="grid lg:grid-cols-2 gap-16">
          <div className="aspect-square bg-[#FFFFFF] rounded-md overflow-hidden border border-stone-200">
            <img src={m.image_url} alt={m.name} className="w-full h-full object-cover" />
          </div>
          <div>
            <div className="label-caps text-stone-500">{m.brand} · {m.category.replace("-", " & ")}</div>
            <h1 className="font-serif-display text-4xl lg:text-5xl text-[#1A362B] tracking-tight mt-3">{m.name}</h1>
            <div className="flex flex-wrap items-center gap-2 mt-4">
              {m.prescription_required && (
                <Badge className="bg-[#1A362B] text-[#EFE7D6] rounded-sm gap-1"><ShieldAlert className="w-3 h-3"/> Prescription required</Badge>
              )}
              {m.stock > 0 ? (
                <Badge className="status-delivered rounded-sm">In stock · {m.stock}</Badge>
              ) : (
                <Badge className="status-cancelled rounded-sm">Out of stock</Badge>
              )}
            </div>
            <div className="flex items-baseline gap-4 mt-6 font-mono-num">
              <span className="text-4xl text-[#1A362B]">{rupees(m.price)}</span>
              {m.mrp > m.price && <span className="text-lg text-stone-400 line-through">{rupees(m.mrp)}</span>}
              {m.mrp > m.price && <span className="text-sm text-[#D96C4A] label-caps">Save {rupees(m.mrp - m.price)}</span>}
            </div>
            <p className="text-stone-700 leading-relaxed mt-6">{m.description}</p>
            {m.dosage && (
              <div className="mt-6">
                <div className="label-caps text-xs text-stone-500 mb-1">Dosage</div>
                <p className="text-sm text-[#1A362B]">{m.dosage}</p>
              </div>
            )}
            <Separator className="my-8" />
            <div className="flex items-center gap-4 mb-6">
              <div className="flex items-center border border-stone-300 rounded-full h-12 px-1">
                <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-10 h-10 rounded-full hover:bg-stone-100" data-testid="detail-qty-dec">−</button>
                <span className="w-10 text-center font-mono-num" data-testid="detail-qty-val">{qty}</span>
                <button onClick={() => setQty(Math.min(m.stock, qty + 1))} className="w-10 h-10 rounded-full hover:bg-stone-100" data-testid="detail-qty-inc">+</button>
              </div>
              <Button
                onClick={async () => { const ok = await addToCart(m, qty); if (ok) navigate("/cart"); }}
                disabled={m.stock === 0}
                className="flex-1 h-12 rounded-full bg-[#D96C4A] hover:bg-[#E67D5C] text-white active:scale-95 transition-transform"
                data-testid="add-to-cart-detail"
              >
                Add to cart · {rupees(m.price * qty)}
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-6">
              <div className="flex items-start gap-3 p-4 rounded-md border border-stone-200">
                <Truck className="w-5 h-5 text-[#1A362B] mt-0.5"/>
                <div>
                  <div className="text-sm font-medium text-[#1A362B]">Same-day delivery</div>
                  <div className="text-xs text-stone-500">Free above ₹500</div>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 rounded-md border border-stone-200">
                <ShieldCheck className="w-5 h-5 text-[#1A362B] mt-0.5"/>
                <div>
                  <div className="text-sm font-medium text-[#1A362B]">Authentic stock</div>
                  <div className="text-xs text-stone-500">Verified batch</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
